/**
 * Web Tools - Frontend-safe RPC-based implementation
 *
 * This module provides web fetch and search tools that work via RPC calls
 * to the backend worker. It does NOT import puppeteer or any Node.js-only dependencies.
 */

import { ToolCategory } from 'tool_sdk/ToolCategory'
import { forwardCallByName } from 'workers-common'

// Tool definitions (inline, no import from tool_sdk to avoid puppeteer)
export const fetchDefinition = {
  name: 'fetch',
  description: `Fetch content from a URL and extract relevant information.

Supports multiple strategies:
- Smart fetching (default): Intelligent content extraction
- Raw fetching: Direct HTTP requests
- Puppeteer: JavaScript-heavy sites
- Jina Reader: SPA and complex sites

Returns structured content including:
- Main text content
- Title and metadata
- Links and images (if requested)
- Raw HTML (if requested)

Usage: Provide a URL and optionally specify extraction options.`,
  parameters: {
    type: 'object',
    properties: {
      url: {
        type: 'string',
        description: 'URL to fetch',
      },
      strategy: {
        type: 'string',
        description: 'Force specific strategy: "raw", "smart", "jina", "puppeteer".',
      },
      maxLength: {
        type: 'number',
        description: 'Maximum content length in characters (default: 10000)',
      },
      extractLinks: {
        type: 'boolean',
        description: 'Extract links from the page (default: false)',
      },
      extractMetadata: {
        type: 'boolean',
        description: 'Extract metadata like title, description (default: true)',
      },
    },
    required: ['url'],
  },
}

export const webSearchDefinition = {
  name: 'webSearch',
  description: `Search the web using multiple search engines.

Features:
- Multi-engine support (DuckDuckGo, Google, Xiaohongshu, Zhihu)
- Vertical search (travel, shopping, lifestyle)
- Automatic result ranking and relevance scoring
- Optional content fetching for top results

Returns search results with:
- Title, URL, snippet
- Relevance scores
- Full content (if fetchResults: true)

Usage: Provide a search query and optionally specify engine and options.`,
  parameters: {
    type: 'object',
    properties: {
      query: {
        type: 'string',
        description: 'Search query',
      },
      engine: {
        type: 'string',
        enum: ['duckduckgo', 'google', 'xiaohongshu', 'zhihu'],
        description: 'Search engine (default: duckduckgo)',
      },
      limit: {
        type: 'number',
        description: 'Max results (default: 10)',
      },
      fetchResults: {
        type: 'boolean',
        description: 'Fetch full content for top results (default: false)',
      },
    },
    required: ['query'],
  },
}

/**
 * Create Web category for ToolCategory system (RPC-based)
 * @param {Object} config
 * @param {Function} config.getWorkerClient - Function to get WorkerClient instance
 * @param {string} [config.workerName='myagent-worker'] - Target worker name for RPC calls
 * @returns {ToolCategory} Web tool category
 */
export function createWebCategory({ getWorkerClient, workerName = 'myagent-worker' }) {
  const callWeb = async (ctx, method, args) => {
    const workerClient = getWorkerClient()
    return forwardCallByName(ctx, workerClient, workerName, `web.${method}`, [args])
  }

  const fromCall = (method) => (ctx, args) => callWeb(ctx, method, args)

  return new ToolCategory({
    id: 'web',
    description: `Fetch web pages and search the internet`,
    tools: [
      { definition: fetchDefinition, implementation: fromCall('fetch') },
      { definition: webSearchDefinition, implementation: fromCall('search') },
    ],
    subCategories: [],
  })
}
